/**
 * NotificationCenter — unified notification inbox panel.
 *
 * Slides out from the right side of the screen. Shows all notifications
 * grouped by type: mentions, approvals, deadlines, Felix alerts, system.
 *
 * Features:
 * - Unread count badge (animated pulse when new)
 * - Click notification to navigate to related entity
 * - Mark as read / dismiss individual or all
 * - Real-time polling (30s interval)
 * - Stagger entrance animation
 * - Glassmorphism panel
 * - Empty state with Felix suggestion
 */

import { useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, m } from "framer-motion";
import {
  Bell, X, Check, CheckCheck, Trash2,
  MessageSquare, Clock, AlertTriangle, Bot,
  Settings, ChevronRight, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { notificationsApi } from "@/api/services";
import type { Notification } from "@/api/types";
import { notificationTypeConfig } from "@/lib/notificationType";
import { cn } from "@/lib/utils";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";

// ── Notification type config ───────────────────────────────────────
//
// MOVED OUT, and not for tidiness. The table that used to live here held six
// keys — mention, approval, deadline, alert, felix, system — matched by
// substring. Measured against production 2026-08-18: ALL TWELVE distinct
// notification types matched none of them and fell through to `system`, so
// every one of 7,784 rows rendered as the same grey gear labelled "System" and
// 219 ERRORs looked exactly like 973 routine INFOs.
//
// It never threw, which is why it lasted: the lookup ran, returned a config and
// drew an icon. It just always returned the same one. See
// lib/notificationType.ts, whose tests replay the real vocabulary.
const getTypeConfig = notificationTypeConfig;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Panel component ────────────────────────────────────────────────

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const panelRef = useModalA11y<HTMLDivElement>({ open, onClose });

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsApi.list({ limit: 50 }),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const notifications = data?.items ?? [];
  const unreadCount = notifications.filter((n) => !n.read_at && !n.dismissed_at).length;

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const handleClick = useCallback((notification: Notification) => {
    if (!notification.read_at) {
      markReadMutation.mutate(notification.id);
    }
    onClose();
    // MT-01 — a notification that carries a target (e.g. the meeting
    // ACTION_ITEM_ASSIGNED digest → /projects/{id}/tasks) navigates
    // there on click, so "things you committed to" open the task
    // directly instead of dead-ending in the notification bell.
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  }, [markReadMutation, onClose, navigate]);

  const markAllRead = useCallback(() => {
    notifications
      .filter((n) => !n.read_at)
      .forEach((n) => markReadMutation.mutate(n.id));
  }, [notifications, markReadMutation]);

  const activeNotifications = useMemo(
    () => notifications.filter((n) => !n.dismissed_at),
    [notifications],
  );

  // Portal to document.body so the panel escapes the ActivityRibbon's
  // backdrop-filter containing block (which traps fixed positioning).
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <m.div
            ref={panelRef}
            {...dialogA11yProps({ labelledById: "notification-center-title" })}
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[400px] bg-white dark:bg-slate-900/95 backdrop-blur-xl border-l border-slate-200/70 dark:border-slate-800 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200/70 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-brand-500/10">
                  <Bell className="h-4 w-4 text-brand-500" />
                </div>
                <h2 id="notification-center-title" className="text-sm font-semibold">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-brand-600 text-[10px] font-bold text-white">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7 px-2">
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Mark all read
                  </Button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto">
              {activeNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="p-4 rounded-2xl bg-emerald-500/10 mb-4">
                    <Inbox className="h-8 w-8 text-emerald-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">All caught up</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-[240px]">
                    You have no new notifications. Felix will alert you when something needs your attention.
                  </p>
                </div>
              ) : (
                <div className="py-2">
                  {activeNotifications.map((notification, i) => {
                    const config = getTypeConfig(notification.notification_type);
                    const Icon = config.icon;
                    const isUnread = !notification.read_at;

                    return (
                      <m.div
                        key={notification.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className={cn(
                          "group flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors",
                          isUnread
                            ? "bg-brand-50/50 dark:bg-brand-900/20 hover:bg-brand-100/50 dark:hover:bg-brand-900/20"
                            : "hover:bg-slate-50 dark:hover:bg-slate-800/40",
                        )}
                        onClick={() => handleClick(notification)}
                      >
                        {/* Icon */}
                        <div className={cn(
                          "shrink-0 mt-0.5 p-1.5 rounded-lg",
                          isUnread ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800/40",
                        )}>
                          <Icon className={cn("h-3.5 w-3.5", config.color)} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "text-xs leading-relaxed",
                            isUnread ? "text-slate-900 dark:text-slate-100 font-medium" : "text-slate-600 dark:text-slate-300",
                          )}>
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">
                              {timeAgo(notification.created_at)}
                            </span>
                            <span className={cn("text-[9px] font-medium uppercase tracking-wide", config.color)}>
                              {config.label}
                            </span>
                          </div>
                        </div>

                        {/* Unread dot + dismiss */}
                        <div className="flex items-center gap-1 shrink-0">
                          {isUnread && (
                            <div className="h-2 w-2 rounded-full bg-brand-500" />
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); dismissMutation.mutate(notification.id); }}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-all"
                            aria-label="Dismiss"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </m.div>
                    );
                  })}
                </div>
              )}
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ── Bell trigger button (used in ribbon/topbar) ────────────────────

export function NotificationBell() {
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsApi.list({ limit: 50 }),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const unreadCount = (data?.items ?? []).filter((n) => !n.read_at && !n.dismissed_at).length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 rounded-lg text-sidebar-text/60 hover:text-sidebar-text-active hover:bg-sidebar-hover transition-colors"
        aria-label="Open notifications"
      >
        <Bell className="h-4.5 w-4.5" />
        {unreadCount > 0 && (
          <m.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-4 px-0.5 rounded-full bg-red-500 text-[9px] font-bold text-white shadow-sm"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </m.span>
        )}
      </button>
      <NotificationCenter open={open} onClose={() => setOpen(false)} />
    </>
  );
}
